import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Progress,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useState } from 'react';

import { useBankHealth, useSetQuestionLifecycle } from '../hooks/use-content';
import { useT, type MessageKey, type TranslateFn } from '../i18n';
import type { BankHealthItem, QuestionLifecycle } from '../lib/api';
import { errorMessage } from '../lib/ui';

/**
 * PRE-261 — bank health.
 *
 * The goal, in the issue's words: learn that an item has gone bad from this
 * screen rather than from a learner complaining. Everything here is a finding
 * the machine computed and nothing here acts on its own — the buttons are the
 * whole point, because what the bank measures is a product decision and an item
 * removed by a cron is a measurement that changed with nobody's agreement.
 *
 * Two lists, answering two different questions:
 *
 *   - **Needs a look** — items with something measurably wrong. Ordered worst
 *     first, and healthy items are absent rather than listed with an empty
 *     column, because a list you have to filter is a list nobody opens twice.
 *   - **Most asked** — the exposure leak. Not a defect list: it is where the
 *     same questions are reaching every learner, which stops being obvious the
 *     moment the bank is big enough to hide it.
 */

const SEVERITY_COLOR: Record<string, string> = { critical: 'red', warning: 'yellow' };

const STATUS_COLOR: Record<string, string> = {
  published: 'green',
  pretest: 'blue',
  under_review: 'yellow',
  retired: 'gray',
};

/** Trim a prompt to something that fits a row without hiding what it asks. */
const short = (text: string, max = 90) =>
  text.length <= max ? text : `${text.slice(0, max).trimEnd()}…`;

const percent = (share: number) => `${Math.round(share * 100)}%`;

const seconds = (ms: number | null) => (ms == null ? '—' : `${Math.round(ms / 1000)}s`);

/**
 * Labels a lifecycle state or a finding severity, both of which arrive as raw
 * API identifiers. An unrecognised one prints itself rather than a key — a new
 * server-side state should look untranslated, not broken.
 */
function labelOf(
  t: TranslateFn,
  prefix: 'bankHealth.lifecycle' | 'bankHealth.severity',
  value: string,
) {
  const key = `${prefix}.${value}` as MessageKey;
  const translated = t(key);
  return translated === key ? value : translated;
}

/**
 * The decision box.
 *
 * The note is asked for rather than optional-by-omission because it replaces
 * the machine's sentence on the row: once a person has looked, their reason is
 * the one worth keeping, and the next reader needs to know somebody was here.
 */
function LifecycleModal({
  entry,
  target,
  onClose,
  onSubmit,
  pending,
}: {
  entry: BankHealthItem;
  target: QuestionLifecycle;
  onClose: () => void;
  onSubmit: (note: string) => void;
  pending: boolean;
}) {
  const [note, setNote] = useState('');
  const t = useT();
  const machineFinding = entry.findings.map((f) => f.message).join(' ');

  return (
    <Modal
      opened
      onClose={onClose}
      title={t(target === 'retired' ? 'bankHealth.retireTitle' : 'bankHealth.sendForReview')}
    >
      <Stack gap="sm">
        <Text size="sm">{entry.item.prompt}</Text>

        {machineFinding ? (
          <Alert color="gray" title={t('bankHealth.measurementSays')}>
            {machineFinding}
          </Alert>
        ) : null}

        {target === 'retired' ? (
          <Text size="sm" c="dimmed">
            {t('bankHealth.retireNote')}
          </Text>
        ) : null}

        <Textarea
          label={t('bankHealth.why')}
          description={t('bankHealth.whyHint')}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          autosize
          minRows={2}
          maxLength={500}
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            color={target === 'retired' ? 'red' : 'yellow'}
            loading={pending}
            onClick={() => onSubmit(note.trim())}
          >
            {t(target === 'retired' ? 'bankHealth.retire' : 'bankHealth.sendForReview')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function BankHealthScreen() {
  const t = useT();
  const health = useBankHealth();
  const lifecycle = useSetQuestionLifecycle();
  const [pending, setPending] = useState<{
    entry: BankHealthItem;
    target: QuestionLifecycle;
  } | null>(null);

  if (health.isPending) return <Loader />;
  if (health.error) return <Alert color="red">{errorMessage(health.error)}</Alert>;

  const report = health.data!;

  const actions = (entry: BankHealthItem) => (
    <Group gap="xs" wrap="nowrap">
      {entry.item.status !== 'under_review' ? (
        <Button
          size="xs"
          variant="light"
          onClick={() => setPending({ entry, target: 'under_review' })}
        >
          {t('bankHealth.review')}
        </Button>
      ) : null}
      {entry.item.status !== 'retired' ? (
        <Button
          size="xs"
          variant="subtle"
          color="red"
          onClick={() => setPending({ entry, target: 'retired' })}
        >
          {t('bankHealth.retire')}
        </Button>
      ) : null}
    </Group>
  );

  return (
    <Stack gap="lg">
      <Group gap="lg">
        <Card withBorder>
          <Text size="xs" c="dimmed">
            {t('bankHealth.liveItems')}
          </Text>
          <Text fw={700}>{report.liveItems}</Text>
        </Card>
        <Card withBorder>
          <Text size="xs" c="dimmed">
            {t('bankHealth.answersCollected')}
          </Text>
          <Text fw={700}>{report.totalAnswers}</Text>
        </Card>
        <Card withBorder>
          <Text size="xs" c="dimmed">
            {t('bankHealth.medianAnswerTime')}
          </Text>
          <Text fw={700}>{seconds(report.medianDurationMs)}</Text>
        </Card>
      </Group>

      <div>
        <Title order={4}>{t('bankHealth.needsLook')}</Title>
        <Text size="sm" c="dimmed" mb="sm">
          {t('bankHealth.needsLookHint')}
        </Text>
        {report.unhealthy.length === 0 ? (
          <Alert color="green">{t('bankHealth.allHealthy')}</Alert>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('bankHealth.colQuestion')}</Table.Th>
                <Table.Th>{t('common.skill')}</Table.Th>
                <Table.Th>{t('bankHealth.colFindings')}</Table.Th>
                <Table.Th>{t('bankHealth.colAnswers')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {report.unhealthy.map((entry) => (
                <Table.Tr key={entry.item.id}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text size="sm">{short(entry.item.prompt)}</Text>
                      <Badge size="xs" color={STATUS_COLOR[entry.item.status] ?? 'gray'}>
                        {labelOf(t, 'bankHealth.lifecycle', entry.item.status)}
                      </Badge>
                    </Stack>
                  </Table.Td>
                  <Table.Td>{entry.item.skillTitle}</Table.Td>
                  <Table.Td>
                    <Stack gap={4}>
                      {entry.findings.map((f) => (
                        <Group key={f.kind} gap="xs" wrap="nowrap">
                          <Badge size="xs" color={SEVERITY_COLOR[f.severity] ?? 'gray'}>
                            {labelOf(t, 'bankHealth.severity', f.severity)}
                          </Badge>
                          <Text size="xs">{f.message}</Text>
                        </Group>
                      ))}
                    </Stack>
                  </Table.Td>
                  <Table.Td>{entry.item.answers}</Table.Td>
                  <Table.Td>{actions(entry)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </div>

      <div>
        <Title order={4}>{t('bankHealth.mostAsked')}</Title>
        <Text size="sm" c="dimmed" mb="sm">
          {t('bankHealth.mostAskedHint')}
        </Text>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('bankHealth.colQuestion')}</Table.Th>
              <Table.Th>{t('common.skill')}</Table.Th>
              <Table.Th>{t('bankHealth.colExposure')}</Table.Th>
              <Table.Th>{t('bankHealth.colAnswers')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {report.mostExposed.map((entry) => (
              <Table.Tr key={entry.item.id}>
                <Table.Td>
                  <Text size="sm">{short(entry.item.prompt)}</Text>
                </Table.Td>
                <Table.Td>{entry.item.skillTitle}</Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Progress value={entry.exposure * 100} w={80} />
                    <Text size="xs">{percent(entry.exposure)}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>{entry.item.answers}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </div>

      {pending ? (
        <LifecycleModal
          entry={pending.entry}
          target={pending.target}
          pending={lifecycle.isPending}
          onClose={() => setPending(null)}
          onSubmit={(note) => {
            lifecycle.mutate(
              { id: pending.entry.item.id, status: pending.target, note: note || undefined },
              { onSuccess: () => setPending(null) },
            );
          }}
        />
      ) : null}
    </Stack>
  );
}
